/**
 * Table Mode Feature для CrudResource
 * 
 * Додає табличний режим перегляду до списку режимів
 * Створює повнофункціональну таблицю з CRUD операціями
 * 
 * Інтегрується через: CrudResourceContext -> eventManager.emit('onAfterInit') -> AddTableViewModeToList
 */

import { observer } from 'mobx-react-lite'
import React from 'react'
import { TbTable } from 'react-icons/tb'

import { userManager } from '@/Core/Managers'
import { MergeConfigAndPropsTask } from '@/Core/Tasks/Utils/MergeConfigAndPropsTask'
import { getTableColumnsOptionKey, resolveColumnSettings, toRenderColumns } from '@/Core/UI/CrudResource/Helpers/tableSettings'
import { Table } from '@/Core/UI/Table'

import { AddTableModeCrudFeature } from './constants'
import { createMenuItemsFunction } from './menuItemsFactory'


/**
 * Додає табличний режим перегляду до списку режимів
 * Створює повнофункціональну таблицю з CRUD операціями
 * 
 * @param crudContext - контекст CRUD ресурсу
 */
export const AddTableViewModeToList = ({ crudContext }: { crudContext: any }) => {
    let tableProps: any = crudContext.settings?.AddTableViewToListTransformer?.table ?? {}

    // Ініціалізуємо storages в crudContext якщо їх немає
    crudContext.storages ??= {}
    crudContext.storages.tableAddActionsMenuDataSourceItemTransformer ??= {}
    
    // Не перезаписуємо menuItems якщо вони вже є (можуть бути передані через tableProps.storages після merge)
    if (!crudContext.storages.tableAddActionsMenuDataSourceItemTransformer.menuItems) {
        crudContext.storages.tableAddActionsMenuDataSourceItemTransformer.menuItems = createMenuItemsFunction(crudContext)
    } else if (typeof crudContext.storages.tableAddActionsMenuDataSourceItemTransformer.menuItems === 'function') {
        // Якщо це функція-фабрика (приймає crudContext), викликаємо її
        const factory = crudContext.storages.tableAddActionsMenuDataSourceItemTransformer.menuItems
        try {
            // Перевіряємо, чи це фабрика (повертає функцію) чи вже готова функція
            const result = factory(crudContext)
            if (typeof result === 'function') {
                crudContext.storages.tableAddActionsMenuDataSourceItemTransformer.menuItems = result
            }
        } catch (e) {
            console.warn('[tableModeFeature] Помилка при виклику фабрики menuItems:', e)
        }
    }
    
    // Обробка подвійного кліку для перегляду запису
    if (
        crudContext.accessFn &&
        typeof crudContext.accessFn === 'function' &&
        crudContext.actions?.view &&
        crudContext.settings?.isRowDoubleClickDisabled !== true
    ) {
        tableProps.onRow = (record: any) => ({
            onDoubleClick: () => {
                if( crudContext.accessFn({
                    accessMode: 'read',
                    accessItem: record,
                    crudContext: crudContext,
                }))
                    crudContext.actions.view(record)
            },
        })
    }

    // Обробка зміни пагінації
    tableProps.onChange = (props: any) => {
        if (props.pageSize !== crudContext.listContext.pagination.pageSize) {
            crudContext.listContext.setPageSize(props.pageSize)
            crudContext.listContext.setCurrentPage(1)
        } else {
            crudContext.listContext.setCurrentPage(props.current)
        }
        crudContext.listContext.actions.load()
    }

    tableProps = MergeConfigAndPropsTask({
        tableProps,
        crudContextProps: crudContext.props?.table || {},
        crudContextResourceProps: crudContext.resource?.table || {},
    })
    
    // Якщо storages не скопіювалися, копіюємо вручну
    if (crudContext.props?.table?.storages) {
        tableProps.storages = tableProps.storages || {}
        Object.assign(tableProps.storages, crudContext.props.table.storages)
    }
    
    // Копіюємо menuItems з tableProps в crudContext якщо вони є
    if (tableProps.storages?.tableAddActionsMenuDataSourceItemTransformer?.menuItems) {
        let menuItems = tableProps.storages.tableAddActionsMenuDataSourceItemTransformer.menuItems
        
        // Якщо це функція, перевіряємо чи це фабрика
        if (typeof menuItems === 'function') {
            try {
                const result = menuItems(crudContext)
                if (typeof result === 'function') {
                    menuItems = result
                }
            } catch (e) {
                console.warn('[tableModeFeature] Помилка при виклику фабрики після копіювання:', e)
            }
        }
        
        crudContext.storages.tableAddActionsMenuDataSourceItemTransformer.menuItems = menuItems
    }
    
    // Стилі таблиці
    tableProps.style = { width: '100%', ...(tableProps.style || {}) }
    
    // Автогенерація колонок з полів ресурсу
    if (
        crudContext !== undefined &&
        typeof crudContext.resource.fields === 'object' &&
        Object.keys(crudContext.resource.fields).length > 0 &&
        !tableProps?.columns
    ) { 
        const columns: any[] = []
        const showColumns = tableProps.showColumns
        const fieldKeys = showColumns && Array.isArray(showColumns) && showColumns.length > 0
            ? showColumns
            : Object.keys(crudContext.resource.fields)
        
        fieldKeys.forEach((name: string) => {
            const field = crudContext.resource.fields[name]
            if (!field) return // Пропускаємо, якщо поле не знайдено
            
            const column = {
            title: field?.label ?? '',
            dataIndex: name,
            key: name,
            }
            columns.push(column)
        })
        tableProps.columns = columns
    }
    
    // Передаємо listContext в storages для доступу до items
    tableProps.storages = tableProps.storages || {}
    tableProps.storages.listContext = crudContext.listContext
    
    // Також передаємо crudContext для подій
    tableProps.storages.crudContext = crudContext
    
    // Додаємо menuItems з settings якщо є
    if (crudContext.settings?.AddTableViewToListTransformer?.table?.storages?.tableAddActionsMenuDataSourceItemTransformer) {
        tableProps.storages.tableAddActionsMenuDataSourceItemTransformer = 
            crudContext.settings.AddTableViewToListTransformer.table.storages.tableAddActionsMenuDataSourceItemTransformer
    }

    // Створюємо observer обгортку для реактивного dataSource та loading
    const TableObserver = observer((props: any) => {
        // Отримуємо listContext напряму з пропсів для кращої реактивності
        const listContext = props.listContext
        
        // Реактивно отримуємо дані з listContext
        const dataSource = listContext?.items || []
        const loading = listContext?.isLoading
        const basePagination = listContext?.pagination || { current: 1, pageSize: 10, total: 0 }

        const pagination = {
            ...basePagination,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            // showTotal: (total: number) => `Всього: ${total}`,
        }
        
        // Прибираємо listContext з props перед передачею в Table
        const { listContext: _, ...restProps } = props

        // Реактивно застосовуємо персональні налаштування колонок
        // (видимість / ширина / порядок). Базовий набір — restProps.columns.
        const resourceKey = crudContext.resource?.key
        if (resourceKey && Array.isArray(restProps.columns)) {
            const saved = userManager.getUserOption(getTableColumnsOptionKey(resourceKey))
            restProps.columns = toRenderColumns(resolveColumnSettings(restProps.columns, saved))
        }

        return React.createElement(Table, {
            ...restProps,
            dataSource,
            loading,
            pagination,
        })
    })

    // Передаємо listContext напряму як prop для observer
    tableProps.listContext = crudContext.listContext

    // Створення конфігурації табличного режиму
    const tableModeItem = {
        label: AddTableModeCrudFeature.defaultTexts.table,
        icon: TbTable,
        template: TableObserver,
        props: tableProps,
    }
    
    // Додавання режиму до списку доступних режимів
    crudContext.listContext.variantItems = {
        ...(crudContext.listContext.variantItems ?? {}),
        table: tableModeItem,
    }

    // Встановлення режиму за замовчуванням
    if (!crudContext.listContext.variant) {
        crudContext.listContext.setVariant('table')
    }
}
